import React, { useEffect, useRef } from "react";
import { useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import towtruckImg from "../assets/towtruck.png";

interface Particle {
  x: number;
  y: number;
  z: number;
  size: number;
  color: string;
  speedZ: number;
}

interface LightTrail {
  x: number;
  y: number;
  z: number;
  length: number;
  color: string;
  speedZ: number;
}

const AnimatedBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { i18n } = useTranslation();
  const isRTL = i18n.language.startsWith("ar");

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    // Preload the tow truck image (ATV)
    const truck = new Image();
    truck.src = towtruckImg;
    let truckLoaded = false;
    truck.onload = () => {
      truckLoaded = true;
    };

    // Perspective parameters
    const focalLength = 300;
    const horizonY = height * 0.45; // Horizon height
    const centerX = width / 2;

    // Movement speeds
    let baseSpeed = 4;
    let scrollSpeed = 0;
    let lastScrollY = window.scrollY;
    let speedWarp = 0; // Accelerates highway lines when scrolling
    let atvZ = 380;    // Persistent Z-depth for looping ATV driving

    // Particle Stars (Floating space particles)
    const numStars = isMobile ? 80 : 250; // Increased star count
    const stars: Particle[] = [];
    for (let i = 0; i < numStars; i++) {
      stars.push({
        x: (Math.random() - 0.5) * width * 2,
        y: -Math.random() * height * 0.5 - 50, // above horizon
        z: Math.random() * 400 + 50,
        size: Math.random() * 1.5 + 0.5,
        color: "rgba(255, 174, 0, 0.4)",
        speedZ: 0.1,
      });
    }

    // Highway Road Lines
    const roadLines: { z: number }[] = [];
    const numRoadLines = 6;
    for (let i = 0; i < numRoadLines; i++) {
      roadLines.push({
        z: (i / numRoadLines) * 350 + 20,
      });
    }

    // High-speed light trails (traffic headlights and taillights)
    const numTrails = isMobile ? 8 : 20;
    const trails: LightTrail[] = [];
    const initTrails = () => {
      trails.length = 0;
      for (let i = 0; i < numTrails; i++) {
        const side = Math.random() > 0.5 ? 1 : -1; // left or right of road
        // Left side gets gold/white headlights, right side gets red taillights
        const color = side < 0
          ? `rgba(255, 174, 0, ${Math.random() * 0.35 + 0.15})` // Golden headlights
          : `rgba(255, 50, 0, ${Math.random() * 0.35 + 0.15})`;   // Red taillights
        
        trails.push({
          x: side * (Math.random() * 80 + 90), // distance from road center
          y: 20, // height relative to road plane
          z: Math.random() * 400 + 10,
          length: Math.random() * 60 + 30,
          color,
          speedZ: Math.random() * 2 + 3,
        });
      }
    };
    initTrails();

    // Mouse tracking for lane steering
    const mouse = {
      x: 0,
      targetX: 0,
      y: 0,
      targetY: 0,
    };

    const handleMouseMove = (event: MouseEvent) => {
      mouse.targetX = (event.clientX - width / 2) / (width / 2); // -1 to 1
      mouse.targetY = (event.clientY - height / 2) / (height / 2); // -1 to 1
    };

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const diff = currentScrollY - lastScrollY;
      scrollSpeed += diff * 0.25; // accumulate scroll speed
      lastScrollY = currentScrollY;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("scroll", handleScroll, { passive: true });

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
      initTrails();
    };
    window.addEventListener("resize", handleResize);

    // Flashing amber beacon state
    let beaconTimer = 0;
    let wheelSpinAngle = 0; // Cumulative wheel spin angle

    // Animation Loop
    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      // Scroll speed dampening
      scrollSpeed *= 0.85;
      speedWarp += (Math.abs(scrollSpeed) - speedWarp) * 0.1;
      const currentSpeed = baseSpeed + speedWarp * 1.5;

      // Mouse smoothing
      mouse.x += (mouse.targetX - mouse.x) * 0.04;
      mouse.y += (mouse.targetY - mouse.y) * 0.04;

      beaconTimer += 0.08;
      wheelSpinAngle += currentSpeed * 0.15; // Smooth incremental wheel rotation

      // -------------------------------------------------------------
      // ATV Driving Progress & Coordinates
      // -------------------------------------------------------------
      atvZ -= currentSpeed * 0.28; // Reduced speed along Z axis
      if (atvZ < 45) {
        atvZ = 380; // Loop back
      }

      const scale = focalLength / atvZ;
      
      // Scaled down ATV sizes (desktop width 75px instead of 110px, mobile width 35px instead of 48px)
      const truckWidth = (isMobile ? 35 : 75) * scale;
      const truckHeight = truckWidth * (682 / 1024); // 3:2 Aspect Ratio

      // Combine a subtle automated idle sway with interactive mouse-based steering
      const idleSway = Math.sin(Date.now() * 0.0008) * 3;
      const mouseSway = mouse.x * 55; // 55px max interactive steering sway
      const sway = idleSway + mouseSway;
      const targetTruckX = centerX + (sway * scale) - truckWidth / 2;

      // Vertical position shifts slightly with mouse Y position and idle vibration
      const mouseVertical = mouse.y * 12; // 12px max interactive vertical shift
      const vibration = Math.sin(Date.now() * 0.065) * 0.5 + mouseVertical;
      const targetTruckY = horizonY + (22 * scale) - truckHeight + vibration;

      let opacity = 1;
      if (atvZ > 300) {
        opacity = (380 - atvZ) / 80;
      } else if (atvZ < 95) {
        opacity = (atvZ - 45) / 50;
      }
      opacity = Math.max(0, Math.min(1, opacity));

      // Headlight positions for beams & glows
      const leftHeadlightX = targetTruckX + truckWidth * 0.28;
      const rightHeadlightX = targetTruckX + truckWidth * 0.72;
      const headlightsY = targetTruckY + truckHeight * 0.62;

      // -------------------------------------------------------------
      // 1. Draw Starry Night Sky (Constellation stars drifting)
      // -------------------------------------------------------------
      stars.forEach((star) => {
        star.z -= star.speedZ * (1 + speedWarp * 0.1);
        if (star.z <= 0) {
          star.z = 400;
        }

        const scaleStar = focalLength / star.z;
        const sx = centerX + star.x * scaleStar;
        const sy = horizonY + star.y * scaleStar;

        if (sx > 0 && sx < width && sy > 0 && sy < horizonY) {
          ctx.fillStyle = star.color;
          ctx.beginPath();
          ctx.arc(sx, sy, star.size * scaleStar * 0.8, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      // -------------------------------------------------------------
      // 2. Draw 3D Perspective Highway Road (MORE REALISTIC TEXTURED ROAD)
      // -------------------------------------------------------------
      const roadWidth = 240;

      // A. Draw textured asphalt base with gradient
      const roadGrad = ctx.createLinearGradient(0, horizonY, 0, height);
      roadGrad.addColorStop(0, "#060606");   // horizon is deep black
      roadGrad.addColorStop(0.3, "#0d0c0c"); // deep charcoal
      roadGrad.addColorStop(1, "#181818");   // textured grey closer
      ctx.fillStyle = roadGrad;
      ctx.beginPath();
      ctx.moveTo(centerX - 3, horizonY);
      ctx.lineTo(centerX + 3, horizonY);
      ctx.lineTo(centerX + roadWidth * 3.6, height);
      ctx.lineTo(centerX - roadWidth * 3.6, height);
      ctx.closePath();
      ctx.fill();

      // B. Draw side gravel shoulders
      // Left shoulder
      const leftShoulderGrad = ctx.createLinearGradient(centerX - roadWidth, horizonY, centerX - roadWidth * 3.6, height);
      leftShoulderGrad.addColorStop(0, "#080706");
      leftShoulderGrad.addColorStop(1, "#12100e");
      ctx.fillStyle = leftShoulderGrad;
      ctx.beginPath();
      ctx.moveTo(centerX - 3, horizonY);
      ctx.lineTo(centerX - roadWidth * 3.0, height);
      ctx.lineTo(centerX - roadWidth * 3.6, height);
      ctx.lineTo(centerX - 3, horizonY);
      ctx.closePath();
      ctx.fill();

      // Right shoulder
      const rightShoulderGrad = ctx.createLinearGradient(centerX + roadWidth, horizonY, centerX + roadWidth * 3.6, height);
      rightShoulderGrad.addColorStop(0, "#080706");
      rightShoulderGrad.addColorStop(1, "#12100e");
      ctx.fillStyle = rightShoulderGrad;
      ctx.beginPath();
      ctx.moveTo(centerX + 3, horizonY);
      ctx.lineTo(centerX + roadWidth * 3.0, height);
      ctx.lineTo(centerX + roadWidth * 3.6, height);
      ctx.lineTo(centerX + 3, horizonY);
      ctx.closePath();
      ctx.fill();

      // C. Draw solid white side stripe markers (glowing edge markers)
      ctx.lineWidth = 1.8;
      ctx.strokeStyle = "rgba(235, 235, 235, 0.6)"; // solid white road edge line
      ctx.beginPath();
      // Left side stripe
      ctx.moveTo(centerX - 3, horizonY);
      ctx.lineTo(centerX - roadWidth * 3.0, height);
      // Right side stripe
      ctx.moveTo(centerX + 3, horizonY);
      ctx.lineTo(centerX + roadWidth * 3.0, height);
      ctx.stroke();

      // D. Draw headlight beam cast on road surface (Realistic light cast)
      if (truckLoaded && opacity > 0.05) {
        ctx.save();
        ctx.globalAlpha = opacity * 0.16; // semi-transparent glow overlay
        
        const drawBeam = (hx: number, hy: number) => {
          const beamGrad = ctx.createLinearGradient(hx, hy, hx, height);
          beamGrad.addColorStop(0, "rgba(255, 235, 180, 0.9)");
          beamGrad.addColorStop(0.4, "rgba(255, 195, 80, 0.35)");
          beamGrad.addColorStop(1, "rgba(255, 195, 80, 0)");
          
          ctx.fillStyle = beamGrad;
          ctx.beginPath();
          ctx.moveTo(hx, hy);
          ctx.lineTo(hx - 90 * scale, height);
          ctx.lineTo(hx + 90 * scale, height);
          ctx.closePath();
          ctx.fill();
        };

        drawBeam(leftHeadlightX, headlightsY);
        drawBeam(rightHeadlightX, headlightsY);
        ctx.restore();
      }

      // E. Draw dashed lane lines
      ctx.lineWidth = 1.6;
      ctx.strokeStyle = "rgba(255, 174, 0, 0.5)"; // Glowing yellow dashed line

      roadLines.forEach((line) => {
        line.z -= currentSpeed * 0.8;
        if (line.z <= 10) {
          line.z = 350;
        }

        const scale1 = focalLength / line.z;
        const scale2 = focalLength / (line.z + 12);

        const y1 = horizonY + 20 * scale1;
        const y2 = horizonY + 20 * scale2;

        const lx1 = centerX - (roadWidth * 0.33) * scale1;
        const lx2 = centerX - (roadWidth * 0.33) * scale2;

        const rx1 = centerX + (roadWidth * 0.33) * scale1;
        const rx2 = centerX + (roadWidth * 0.33) * scale2;

        ctx.beginPath();
        ctx.moveTo(lx1, y1);
        ctx.lineTo(lx2, y2);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(rx1, y1);
        ctx.lineTo(rx2, y2);
        ctx.stroke();

        // F. Draw roadside glowing amber reflectors (3D perspective posts)
        ctx.fillStyle = "rgba(255, 120, 0, 0.75)";
        const drawReflector = (rx: number, ry: number, rScale: number) => {
          ctx.beginPath();
          ctx.arc(rx, ry - 3 * rScale, 1.5 * rScale, 0, Math.PI * 2);
          ctx.fill();
        };
        
        // Left shoulder reflector post
        const lShoulderX = centerX - (roadWidth * 3.0) * scale1;
        drawReflector(lShoulderX, y1, scale1);

        // Right shoulder reflector post
        const rShoulderX = centerX + (roadWidth * 3.0) * scale1;
        drawReflector(rShoulderX, y1, scale1);
      });

      // -------------------------------------------------------------
      // 3. Draw Speeding Light Trails (Oncoming and Outgoing Traffic)
      // -------------------------------------------------------------
      trails.forEach((trail) => {
        trail.z -= trail.speedZ * (1 + speedWarp * 0.25);
        if (trail.z <= 5) {
          trail.z = 400;
          trail.length = Math.random() * 60 + 30;
        }

        const scaleStart = focalLength / trail.z;
        const scaleEnd = focalLength / (trail.z + trail.length);

        const sx = centerX + trail.x * scaleStart;
        const sy = horizonY + trail.y * scaleStart;

        const ex = centerX + trail.x * scaleEnd;
        const ey = horizonY + trail.y * scaleEnd;

        if (sy > horizonY && ey > horizonY) {
          ctx.strokeStyle = trail.color;
          ctx.lineWidth = Math.min(6, 2.5 * scaleStart);
          ctx.beginPath();
          ctx.moveTo(sx, sy);
          ctx.lineTo(ex, ey);
          ctx.stroke();
        }
      });

      // -------------------------------------------------------------
      // 4. Draw Interactive Realistic TowHelp ATV (Looping Perspective Drive)
      // -------------------------------------------------------------
      if (truckLoaded && opacity > 0.01) {
        ctx.save();

        ctx.globalAlpha = opacity;

        ctx.translate(targetTruckX + truckWidth / 2, targetTruckY + truckHeight / 2);
        const tiltAngle = sway * 0.0005 * scale;
        ctx.rotate(tiltAngle);

        ctx.drawImage(
          truck,
          -truckWidth / 2,
          -truckHeight / 2,
          truckWidth,
          truckHeight
        );

        ctx.restore();

        // -------------------------------------------------------------
        // 4.1. Draw High-Speed Spinning Tire Blur Over Wheel Areas
        // -------------------------------------------------------------
        const drawWheelBlur = (wx: number, wy: number, rxSize: number, rySize: number) => {
          ctx.save();
          
          // Clip drawing to the tire ellipse boundary
          ctx.beginPath();
          ctx.ellipse(wx, wy, rxSize, rySize, 0, 0, Math.PI * 2);
          ctx.clip();

          // 1. Draw vertical motion blur base (semi-transparent dark gradient)
          const tireGrad = ctx.createLinearGradient(wx, wy - rySize, wx, wy + rySize);
          tireGrad.addColorStop(0, "rgba(5, 5, 5, 0.4)");
          tireGrad.addColorStop(0.5, "rgba(15, 15, 15, 0.7)");
          tireGrad.addColorStop(1, "rgba(5, 5, 5, 0.4)");
          ctx.fillStyle = tireGrad;
          ctx.fill();

          // 2. Draw vertical sliding tread lines to simulate downward spin
          ctx.strokeStyle = "rgba(0, 0, 0, 0.8)";
          ctx.lineWidth = Math.max(1.2, 2.2 * scale);
          
          // Draw 3 tread columns across the tire width
          const columns = [-0.5, 0, 0.5];
          columns.forEach((colFactor) => {
            const colX = wx + rxSize * colFactor;
            
            // Draw 3 sliding tread segments in each column
            for (let i = 0; i < 3; i++) {
              const slideSpeed = 16;
              const totalHeight = rySize * 2;
              const segmentLength = rySize * 0.5;
              
              const yOffset = (wheelSpinAngle * slideSpeed + i * (totalHeight / 3)) % totalHeight;
              const startY = wy - rySize + yOffset;
              const endY = startY + segmentLength;
              
              ctx.beginPath();
              ctx.moveTo(colX, startY);
              ctx.lineTo(colX, Math.min(endY, wy + rySize));
              ctx.stroke();
              
              // Wrap-around segment (if it goes off the bottom, draw the rest at the top)
              if (endY > wy + rySize) {
                ctx.beginPath();
                ctx.moveTo(colX, wy - rySize);
                ctx.lineTo(colX, wy - rySize + (endY - (wy + rySize)));
                ctx.stroke();
              }
            }
          });

          // 3. Apply a blur filter overlay to make it look like a smooth motion blur
          ctx.fillStyle = "rgba(10, 10, 10, 0.25)";
          ctx.filter = `blur(${Math.max(1, 1.8 * scale)}px)`;
          ctx.beginPath();
          ctx.ellipse(wx, wy, rxSize, rySize, 0, 0, Math.PI * 2);
          ctx.fill();

          ctx.restore();
        };

        const leftWheelX = targetTruckX + truckWidth * 0.16;
        const leftWheelY = targetTruckY + truckHeight * 0.76;
        const rightWheelX = targetTruckX + truckWidth * 0.84;
        const rightWheelY = targetTruckY + truckHeight * 0.76;
        
        const wheelRadiusX = truckWidth * 0.10;
        const wheelRadiusY = truckHeight * 0.18;

        // Draw vertical tire spinning blurs
        drawWheelBlur(leftWheelX, leftWheelY, wheelRadiusX, wheelRadiusY);
        drawWheelBlur(rightWheelX, rightWheelY, wheelRadiusX, wheelRadiusY);

        // -------------------------------------------------------------
        // 5. Draw Glowing Lights: Headlight flares & Center Beacon
        // -------------------------------------------------------------
        const drawHeadlightGlow = (hx: number, hy: number) => {
          ctx.beginPath();
          const grad = ctx.createRadialGradient(
            hx,
            hy,
            0,
            hx,
            hy,
            (isMobile ? 18 : 35) * scale
          );
          grad.addColorStop(0, `rgba(255, 235, 180, ${0.8 * opacity})`);
          grad.addColorStop(0.3, `rgba(255, 180, 50, ${0.3 * opacity})`);
          grad.addColorStop(1, `rgba(255, 180, 50, 0)`);
          ctx.fillStyle = grad;
          ctx.arc(hx, hy, (isMobile ? 18 : 35) * scale, 0, Math.PI * 2);
          ctx.fill();
        };

        drawHeadlightGlow(leftHeadlightX, headlightsY);
        drawHeadlightGlow(rightHeadlightX, headlightsY);

        const beaconX = targetTruckX + truckWidth * 0.5;
        const beaconY = targetTruckY + truckHeight * 0.26;

        const flashIntensity = (Math.sin(beaconTimer * 3.5) + 1.2) / 2.2;
        if (flashIntensity > 0.15) {
          ctx.beginPath();
          const beaconGrad = ctx.createRadialGradient(
            beaconX,
            beaconY,
            0,
            beaconX,
            beaconY,
            (isMobile ? 22 : 50) * scale * flashIntensity
          );
          beaconGrad.addColorStop(0, `rgba(255, 174, 0, ${0.9 * opacity})`);
          beaconGrad.addColorStop(0.2, `rgba(255, 120, 0, ${0.5 * opacity})`);
          beaconGrad.addColorStop(0.5, `rgba(255, 120, 0, ${0.15 * opacity})`);
          beaconGrad.addColorStop(1, `rgba(255, 120, 0, 0)`);
          ctx.fillStyle = beaconGrad;
          ctx.arc(
            beaconX,
            beaconY,
            (isMobile ? 22 : 50) * scale * flashIntensity,
            0,
            Math.PI * 2
          );
          ctx.fill();

          ctx.beginPath();
          ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
          ctx.arc(beaconX, beaconY, Math.max(1, 2 * scale), 0, Math.PI * 2);
          ctx.fill();
        }
      } else if (!truckLoaded) {
        ctx.fillStyle = "rgba(255, 174, 0, 0.1)";
        ctx.beginPath();
        ctx.arc(centerX, horizonY + 20, 30, 0, Math.PI * 2);
        ctx.fill();
      }

      animationFrameId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [isMobile, isRTL]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 0,
        pointerEvents: "none",
        mixBlendMode: "screen",
      }}
    />
  );
};

export default AnimatedBackground;
